import { useLayoutEffect, useRef, useState } from 'react';

/**
 * React hook to determine if a referenced HTML element is truncated (i.e., its
 * content overflows its visible area). This can be used to conditionally apply
 * a tooltip to truncated content.
 *
 * Limitation: This hook does not listen for window or element resize events. It
 * only updates when React rerenders the component, so truncation state may not
 * reflect changes caused by resizing until a rerender occurs.
 */
export default function useIsElementTruncated<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const hasTruncation = Boolean(
    ref.current &&
      // This checks for either vertical and horizontal truncation. If
      // necessary, it can be extended to check a single direction.
      (ref.current.scrollHeight > ref.current.clientHeight ||
        ref.current.scrollWidth > ref.current.clientWidth)
  );

  useLayoutEffect(() => {
    setIsTruncated(hasTruncation);
  }, [hasTruncation]);

  return { ref, isTruncated };
}
